import PropTypes from "prop-types";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { READINESS_COLORS } from "../../../../constants";
import {
  ANSWER_MAX,
  buildXTicks,
  READINESS_KEYS,
} from "../../../../utils/readiness-report";
import { QuestionSwitch } from "./ReadinessChartParts";
import { MUTED_COLOR } from "./readinessChartTheme";
import { formatUtcDay, sectionTranslator } from "./readinessFormat";

const CHART_HEIGHT = 260;
const LONG_PERIOD_DAYS = 365;

const tr = sectionTranslator("byQuestion.chart");

// A tick every 5 (the height of one question), up to what the shown questions add up to
const yTicks = (shownCount) =>
  Array.from({ length: shownCount + 1 }, (_, i) => i * ANSWER_MAX);

const rowTotal = (row, shown) =>
  shown.reduce((sum, key) => sum + (row[key] ?? 0), 0);

const QuestionTooltip = ({ active, payload, shown }) => {
  const { t } = useTranslation("athlete-portal");
  const row = active ? payload?.[0]?.payload : null;
  if (!row) return null;
  return (
    <div className="rr-tooltip">
      <div className="rr-tooltip__date">{formatUtcDay(row.utcDate)}</div>
      {shown.length > 1 ? (
        <div className="rr-tooltip__row rr-tooltip__row--strong">
          <span>{t("readinessRate.score")}</span>
          <span>{rowTotal(row, shown)}</span>
        </div>
      ) : null}
      {[...shown].reverse().map((key) => (
        <div key={key} className="rr-tooltip__row">
          <span style={{ color: READINESS_COLORS[key] }}>
            {t(`readinessRate.${key}`)}
          </span>
          <span>{row[key]}</span>
        </div>
      ))}
    </div>
  );
};

QuestionTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  shown: PropTypes.arrayOf(PropTypes.string).isRequired,
};

// The five answers stacked, one layer per question, so together they add up to the day's total.
// One point per questionnaire; the coach picks which questions are in.
export const ReadinessQuestionChart = memo(({ report }) => {
  const { t } = useTranslation("athlete-portal");
  const [shown, setShown] = useState(READINESS_KEYS);
  const rows = useMemo(
    () =>
      report.days
        .filter((day) => day.kind === "questionnaire")
        .map((day, idx) => ({
          idx,
          utcDate: day.utcDate,
          ...Object.fromEntries(
            day.answers.map((answer) => [answer.key, answer.value])
          ),
        })),
    [report.days]
  );
  const xTicks = useMemo(() => buildXTicks(rows.length), [rows.length]);
  const longPeriod = report.period.days > LONG_PERIOD_DAYS;

  // Never switch the last question off: an empty chart says nothing
  const toggle = (key) =>
    setShown((current) => {
      if (!current.includes(key)) {
        return READINESS_KEYS.filter(
          (item) => item === key || current.includes(item)
        );
      }
      return current.length > 1
        ? current.filter((item) => item !== key)
        : current;
    });

  return (
    <div className="rr-chart rr-qchart">
      <div className="rr-chart__header">
        <div className="rr-chart__heading">
          <h3 className="rr-chart__title">{tr(t, "title")}</h3>
          <p className="rr-chart__description">{tr(t, "description")}</p>
        </div>
        <QuestionSwitch
          options={READINESS_KEYS}
          shown={shown}
          onToggle={toggle}
        />
      </div>
      <ResponsiveContainer width="99%" height={CHART_HEIGHT}>
        <ComposedChart
          data={rows}
          margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
        >
          <defs>
            {READINESS_KEYS.map((key) => (
              <linearGradient
                key={key}
                id={`rr-qchart-${key}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor={READINESS_COLORS[key]}
                  stopOpacity={0.85}
                />
                <stop
                  offset="100%"
                  stopColor={READINESS_COLORS[key]}
                  stopOpacity={0.05}
                />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid
            stroke="var(--white-10)"
            strokeDasharray="3 3"
            vertical={false}
          />
          <XAxis
            dataKey="idx"
            type="number"
            domain={[0, Math.max(rows.length - 1, 1)]}
            ticks={xTicks}
            tickFormatter={(idx) =>
              formatUtcDay(
                rows[idx]?.utcDate,
                longPeriod ? "MMM YYYY" : "MMM D"
              )
            }
            tick={{ fontSize: 11, fill: MUTED_COLOR }}
            tickLine={false}
            axisLine={false}
            padding={{ left: 8, right: 8 }}
          />
          <YAxis
            domain={[0, shown.length * ANSWER_MAX]}
            ticks={yTicks(shown.length)}
            tick={{ fontSize: 11, fill: MUTED_COLOR }}
            tickLine={false}
            axisLine={false}
            width={32}
          />
          <Tooltip
            content={<QuestionTooltip shown={shown} />}
            cursor={{ stroke: MUTED_COLOR, strokeWidth: 1 }}
            isAnimationActive={false}
          />
          {READINESS_KEYS.filter((key) => shown.includes(key)).map((key) => (
            <Area
              key={key}
              dataKey={key}
              stackId="questions"
              stroke={READINESS_COLORS[key]}
              strokeWidth={2}
              fill={`url(#rr-qchart-${key})`}
              dot={rows.length === 1}
              activeDot={{ r: 4, fill: READINESS_COLORS[key], stroke: "none" }}
              isAnimationActive={false}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
});

ReadinessQuestionChart.propTypes = {
  report: PropTypes.shape({
    days: PropTypes.array.isRequired,
    period: PropTypes.object.isRequired,
  }).isRequired,
};
